import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import {applyPlugin } from 'jspdf-autotable';
import { useNotificationContext } from '../contexts/NotificationContext';

// Apply jsPDF AutoTable plugin
applyPlugin(jsPDF);

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const PDFEmplois = () => {
  const { showSuccess, showError } = useNotificationContext();
  const [departments, setDepartments] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [currentPdf, setCurrentPdf] = useState(null);
  
  const [filters, setFilters] = useState({
    type: 'classes', // classes, teachers, rooms
    department: '',
    academicYear: '',
    semester: 1,
    target: '' // ID de la classe/enseignant/salle ou 'all'
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (filters.department && filters.academicYear) {
      fetchTargetData();
    }
  }, [filters.type, filters.department, filters.academicYear]);

  const fetchInitialData = async () => {
    try {
      const [deptRes, yearRes] = await Promise.all([
        axios.get(`${API_URL}/api/departments?limit=1000`),
        axios.get(`${API_URL}/api/academic-years?limit=1000`)
      ]);
      setDepartments(deptRes.data);
      setAcademicYears(yearRes.data);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    }
  };

  const fetchTargetData = async () => {
    try {
      if (filters.type === 'classes') {
        const res = await axios.get(`${API_URL}/api/classes?department=${filters.department}&academicYear=${filters.academicYear}&limit=1000`);
        setClasses(Array.isArray(res.data) ? res.data : res.data.data || []);
      } else if (filters.type === 'teachers') {
        const res = await axios.get(`${API_URL}/api/teachers?limit=1000`);
        const teachersData = Array.isArray(res.data) ? res.data : res.data.data || [];
        setTeachers(teachersData.filter(t => t.departments?.some(d => d._id === filters.department)));
      } else if (filters.type === 'rooms') {
        const res = await axios.get(`${API_URL}/api/rooms?limit=1000`);
        setRooms(Array.isArray(res.data) ? res.data : res.data.data || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'type' && { target: '' })
    }));
  };

  const generatePDF = async () => {
    
    if (!filters.department || !filters.academicYear) {
      showError('Veuillez sélectionner le département et l\'année universitaire');
      return;
    }

    setLoading(true);
    try {
      const sessions = await fetchSessions();
      const pdf = await createPDF(sessions);
      const pdfBlob = pdf.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
      setCurrentPdf(pdf);
      showSuccess('PDF généré avec succès');
    } catch (error) {
      console.error('Erreur lors de la génération:', error);
      showError('Erreur lors de la génération du PDF');
    } finally {
      setLoading(false);
    }
  };

  const printPDF = () => {
    if (currentPdf) {
      const targetItem = getTargetOptions().find(item => item._id === filters.target);
      const fileName = `Emploi_du_temps_${getTargetLabel(targetItem || {})}.pdf`;
      currentPdf.save(fileName);
    }
  };

  const fetchSessions = async () => {
    const params = new URLSearchParams({
      semester: filters.semester,
      academicYear: filters.academicYear,
      limit: 10000, // Récupérer toutes les sessions sans pagination
      nocache: 'true' // Forcer le bypass du cache
    });

    if (filters.target && filters.target !== 'all') {
      if (filters.type === 'classes') params.append('class', filters.target);
      else if (filters.type === 'teachers') params.append('teacher', filters.target);
      else if (filters.type === 'rooms') params.append('room', filters.target);
    }

    const response = await axios.get(`${API_URL}/api/sessions?${params}`);
    // Toujours retourner un tableau
    return Array.isArray(response.data) ? response.data : response.data.data || [];
  };

  const createPDF = async (sessions) => {
    const doc = new jsPDF('l', 'mm', 'a4');
    // Récupérer les données nécessaires pour l'en-tête
    const universityRes = await axios.get(`${API_URL}/api/university`).catch(() => ({ data: null }));
    
    const university = universityRes.data;
    
    if (filters.target === 'all') {
      // Générer pour tous les éléments
      return await generateAllPDFs(doc, sessions, university);
    } else {
      // Générer pour un élément spécifique
      const targetItem = await getTargetItem();
      if (targetItem) {
        return generateSinglePDF(doc, sessions, targetItem, university);
      }
    }
    return doc;
  };

  const generateHeaderContent = (type, item, department, academicYear, university) => {
    const universityLocation = university?.name?.split(' ').pop() || 'GAFSA';
    
    const headerLines = [
      `INSTITUT SUPÉRIEUR DES ÉTUDES TECHNOLOGIQUES DE ${universityLocation.toUpperCase()}`,
      '',
      'EMPLOI DU TEMPS',
      ''
    ];
    
    if (type === 'classes') {
      headerLines.push(`Classe: ${item.name}`);
    } else if (type === 'teachers') {
      headerLines.push(`Enseignant: ${item.firstName} ${item.lastName}`);
      if (item.grade?.name) {
        headerLines.push(`Grade: ${item.grade.name}`);
      }
    } else if (type === 'rooms') {
      headerLines.push(`Salle: ${item.name}`);
      if (item.capacity) {
        headerLines.push(`Capacité: ${item.capacity} places`);
      }
    }
    
    headerLines.push(`Département: ${department?.name || ''}`);
    headerLines.push(`AU: ${academicYear?.name || ''} Semestre: ${filters.semester}`);
    
    return headerLines;
  };

  const generateHeader = (pdf, item, type, university, templates, sessions) => {
    const academicYear = academicYears.find(y => y._id === filters.academicYear);
    const department = departments.find(d => d._id === filters.department);
    
    const headerLines = generateHeaderContent(type, item, department, academicYear, university);
    
    headerLines.forEach((line, index) => {
      if (line.includes('INSTITUT SUPÉRIEUR')) {
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'bold');
      } else if (line.includes('EMPLOI DU TEMPS')) {
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
      } else if (line.includes('Classe:') || line.includes('Enseignant:') || line.includes('Salle:')) {
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
      } else if (line.trim()) {
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
      }
      
      if (line.trim()) {
        const textWidth = pdf.getTextWidth(line.trim());
        let yPos = 10 + (index * 4);
        
        if (line.includes('EMPLOI DU TEMPS')) yPos += 2;
        
        pdf.text(line.trim(), (297 - textWidth) / 2, yPos);
      }
    });
    
    // Ajouter les totaux pour les enseignants et les classes
    if ((type === 'teachers' || type === 'classes') && sessions.length > 0) {
      // Filtrer les sessions selon le type
      let filteredSessions;
      if (type === 'teachers') {
        filteredSessions = sessions.filter(s => s.teacher?._id === item._id);
      } else {
        filteredSessions = sessions.filter(s => s.class?._id === item._id);
      }
      
      // Calculer les totaux d'heures selon la formule
      let totalHours = 0, coursHours = 0, tdHours = 0, tpHours = 0;
      
      filteredSessions.forEach(session => {
        if (session.type === 'LECTURE') {
          // Cours : 1.5h => 1h cours + 0.5h TD
          totalHours += 1.5;
          coursHours += 1;
          tdHours += 0.5;
        } else if (session.type === 'PRACTICAL') {
          // TP : 1.5h => 1.5h TP
          totalHours += 1.5;
          tpHours += 1.5;
        } else {
          // TD : 1.5h => 1.5h TD
          totalHours += 1.5;
          coursHours += 1;
          tdHours += 0.5;
        }
      });
      
      // Afficher les totaux à droite
      pdf.setFontSize(8);
      pdf.setFont(undefined, 'normal');
      const totalsText = `Total: ${totalHours}h, Cours: ${coursHours}h, TD: ${tdHours}h, TP: ${tpHours}h`;
      const totalsWidth = pdf.getTextWidth(totalsText);
      pdf.text(totalsText, 297 - 15 - totalsWidth, 35);
    }
  };

  const generateTable = (pdf, sessions, targetId = null) => {
    // Filtrer les sessions selon le type et l'élément sélectionné
    let filteredSessions = sessions;
    
    if (targetId && filters.target !== 'all') {
      if (filters.type === 'classes') {
        filteredSessions = sessions.filter(s => s.class?._id === targetId);
      } else if (filters.type === 'teachers') {
        filteredSessions = sessions.filter(s => s.teacher?._id === targetId);
      } else if (filters.type === 'rooms') {
        filteredSessions = sessions.filter(s => s.room?._id === targetId);
      }
    }
    
    const timeSlots = ['8h30-10h00', '10h10-11h40', '11h50-13h20', '13h30-15h00', '15h10-16h40', '16h50-18h20'];
    const days = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const tableData = [];
    
    timeSlots.forEach((timeSlot, timeIndex) => {
      const row = [timeSlot];
      days.forEach((day, dayIndex) => {
        const sessionsForSlot = filteredSessions.filter(s => 
          s.dayOfWeek === dayIndex + 1 && s.timeSlot === timeIndex + 1
        );
        
        if (sessionsForSlot.length > 0) {
          row.push({ content: '', sessions: sessionsForSlot });
        } else {
          row.push('');
        }
      });
      tableData.push(row);
    });
    
    pdf.autoTable({
      head: [['Horaires', ...days]],
      body: tableData,
      startY: 42,
      tableWidth: 'wrap',
      margin: { left: 15, right: 15 },
      styles: { 
        fontSize: 8, 
        cellPadding: 4,
        halign: 'center',
        valign: 'middle',
        lineColor: [0, 0, 0],
        lineWidth: 0.5,
        minCellHeight: 20
      },
      headStyles: { 
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontSize: 8,
        fontStyle: 'bold',
        lineWidth: 1,
        minCellHeight: 12
      },
      columnStyles: { 
        0: { cellWidth: 25, fillColor: [240, 240, 240], fontStyle: 'bold' },
        1: { cellWidth: 40.25 }, 2: { cellWidth: 40.25 }, 3: { cellWidth: 40.25 },
        4: { cellWidth: 40.25 }, 5: { cellWidth: 40.25 }, 6: { cellWidth: 40.25 }
      },
      didDrawCell: function(data) {
        if (data.cell.raw && data.cell.raw.sessions) {
          const sessions = data.cell.raw.sessions;
          const cellX = data.cell.x, cellY = data.cell.y;
          const cellWidth = data.cell.width, cellHeight = data.cell.height;
          
          sessions.forEach((session, index) => {
            const sessionHeight = cellHeight / sessions.length;
            const yOffset = index * sessionHeight;
            
            if (index > 0) {
              pdf.setLineWidth(0.3);
              pdf.line(cellX, cellY + yOffset, cellX + cellWidth, cellY + yOffset);
            }
            
            // Code de la matière en haut - taille adaptée selon le type d'emploi
            if (session.course) {
              const fontSize = filters.type === 'classes' ? 9 : 7;
              pdf.setFontSize(fontSize);
              pdf.setFont(undefined, 'bold');
              const courseText = session.course.code;
              const courseWidth = pdf.getTextWidth(courseText);
              pdf.text(courseText, cellX + (cellWidth - courseWidth) / 2, cellY + yOffset + 5);
            }
            
            // Affichage selon le type d'emploi du temps
            const bottomTexts = [];
            if (filters.type === 'teachers') {
              // Pour les emplois d'enseignants, afficher la classe + groupe
              if (session.class) {
                let classText = session.class.name;
                if (session.group && session.group.trim() !== '') {
                  const groupText = session.group.replace('Groupe ', 'G');
                  classText += ` ${groupText}`;
                }
                bottomTexts.push(classText);
              }
              if (session.room) {
                bottomTexts.push(session.room.name);
              }
            } else if (filters.type === 'rooms') {
              // Pour les emplois de salles, afficher la classe + groupe et l'enseignant
              if (session.class) {
                let classText = session.class.name;
                if (session.group && session.group.trim() !== '') {
                  const groupText = session.group.replace('Groupe ', 'G');
                  classText += ` ${groupText}`;
                }
                bottomTexts.push(classText);
              }
              if (session.teacher) {
                const teacherText = `${session.teacher.firstName.charAt(0)}.${session.teacher.lastName}`;
                bottomTexts.push(teacherText);
              }
            } else {
              // Pour les emplois de classes, afficher l'enseignant, la salle et le groupe
              if (session.teacher) {
                const teacherText = `${session.teacher.firstName.charAt(0)}.${session.teacher.lastName}`;
                bottomTexts.push(teacherText);
              }
              if (session.room) {
                bottomTexts.push(session.room.name);
              }
              // Ajouter le groupe pour les emplois de classes
              if (session.group && session.group.trim() !== '') {
                const groupText = session.group.replace('Groupe ', 'G');
                bottomTexts.push(groupText);
              }
            }
            
            if (bottomTexts.length > 0) {
              const fontSize = filters.type === 'classes' ? 7 : 6;
              pdf.setFontSize(fontSize);
              pdf.setFont(undefined, 'normal');
              const combinedText = bottomTexts.join(' - ');
              const textWidth = pdf.getTextWidth(combinedText);
              pdf.text(combinedText, cellX + cellWidth - textWidth - 1, cellY + yOffset + sessionHeight - 2);
            }
            

          });
        }
      }
    });
  };

  const generateFooter = (pdf, university) => {
    const pageHeight = pdf.internal.pageSize.height;
    const footerY = pageHeight - 25;
    
    pdf.setFontSize(8);
    pdf.setFont(undefined, 'bold');
    
    pdf.rect(15, footerY, 75, 20);
    pdf.rect(105, footerY, 75, 20);
    pdf.rect(195, footerY, 75, 20);
    
    pdf.text('Directeur de Département', 18, footerY + 6);
    pdf.text('Directeur des Études', 108, footerY + 6);
    pdf.text('Directeur de l\'ISET', 198, footerY + 6);
    
    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(7);
    const department = departments.find(d => d._id === filters.department);
    pdf.text(department?.head || '', 18, footerY + 15);
    pdf.text(university?.studiesDirectorName || '', 108, footerY + 15);
    pdf.text(university?.directorName || '', 198, footerY + 15);
    
    pdf.setFontSize(6);
    pdf.text('Code ISO: FQ-PED-06 (DTI)/REV00', 200, pageHeight - 5);
  };

  const generateSinglePDF = (pdf, sessions, item, university) => {
    const type = filters.type === 'classes' ? 'classes' : filters.type === 'teachers' ? 'teachers' : 'rooms';
    generateHeader(pdf, item, type, university, {}, sessions);
    generateTable(pdf, sessions, item._id);
    generateFooter(pdf, university);
    
    return pdf;
  };

  const generateAllPDFs = async (pdf, allSessions, university) => {
    const targets = getTargetOptions();
    
    for (let i = 0; i < targets.length; i++) {
      const target = targets[i];
      let targetSessions = [];
      
      if (filters.type === 'classes') {
        targetSessions = allSessions.filter(s => s.class?._id === target._id);
      } else if (filters.type === 'teachers') {
        targetSessions = allSessions.filter(s => s.teacher?._id === target._id);
      } else if (filters.type === 'rooms') {
        targetSessions = allSessions.filter(s => s.room?._id === target._id);
      }
      
      if (i > 0) pdf.addPage();
      
      const type = filters.type === 'classes' ? 'classes' : filters.type === 'teachers' ? 'teachers' : 'rooms';
      generateHeader(pdf, target, type, university, {}, targetSessions);
      generateTable(pdf, targetSessions, target._id);
      generateFooter(pdf, university);
    }
    
    return pdf;
  };

  const getTargetItem = async () => {
    if (filters.type === 'classes') {
      return classes.find(c => c._id === filters.target);
    } else if (filters.type === 'teachers') {
      return teachers.find(t => t._id === filters.target);
    } else if (filters.type === 'rooms') {
      return rooms.find(r => r._id === filters.target);
    }
    return null;
  };

  const getTargetOptions = () => {
    if (filters.type === 'classes') return classes;
    if (filters.type === 'teachers') return teachers;
    if (filters.type === 'rooms') return rooms;
    return [];
  };

  const getTargetLabel = (item) => {
    if (filters.type === 'classes') return item.name;
    if (filters.type === 'teachers') return `${item.firstName} ${item.lastName}`;
    if (filters.type === 'rooms') return item.name;
    return '';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Génération PDF - Emplois du temps</h1>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type d'emploi</label>
              <select 
                name="type" 
                value={filters.type} 
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="classes">Classes</option>
                <option value="teachers">Enseignants</option>
                <option value="rooms">Salles</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Département</label>
              <select 
                name="department" 
                value={filters.department} 
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Sélectionner un département</option>
                {departments.map(dept => (
                  <option key={dept._id} value={dept._id}>{dept.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Année universitaire</label>
              <select 
                name="academicYear" 
                value={filters.academicYear} 
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Sélectionner une année</option>
                {academicYears.map(year => (
                  <option key={year._id} value={year._id}>{year.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Semestre</label>
              <select 
                name="semester" 
                value={filters.semester} 
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value={1}>Semestre 1</option>
                <option value={2}>Semestre 2</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {filters.type === 'classes' ? 'Classe' : filters.type === 'teachers' ? 'Enseignant' : 'Salle'}
              </label>
              <select 
                name="target" 
                value={filters.target} 
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Sélectionner</option>
                <option value="all">Tous</option>
                {getTargetOptions().map(item => (
                  <option key={item._id} value={item._id}>{getTargetLabel(item)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-6">
            <button 
              onClick={generatePDF} 
              disabled={loading || !filters.department || !filters.academicYear}
              className={`px-6 py-3 text-sm font-medium text-white rounded-lg focus:ring-2 focus:ring-primary-500 ${
                loading || !filters.department || !filters.academicYear
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Génération...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  📄 Générer PDF
                </span>
              )}
            </button>
            
            {pdfUrl && (
              <button 
                onClick={printPDF}
                className="px-6 py-3 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500"
              >
                <span className="flex items-center justify-center gap-2">
                  💾 Télécharger PDF
                </span>
              </button>
            )}
          </div>
        </div>

        {pdfUrl && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">👁️ Aperçu de l'emploi du temps</h3>
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <iframe 
                src={pdfUrl}
                className="w-full h-96"
                title="Aperçu PDF"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFEmplois;